import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Clock,
  Download,
  EyeOff,
  FileCode2,
  ImageIcon,
  Monitor,
  Printer,
  QrCode,
} from "lucide-react";
import Seo from "@/components/Seo";
import { CONTACT_EMAIL } from "@/lib/config";

const TILE = {
  dark: "bg-[#2A1A17] border-[#F5F1E8]/12",
  light: "bg-[#F5F1E8] border-[#3B2523]/12",
};

const prettySize = (kb) =>
  kb >= 1024 ? `${(kb / 1024).toFixed(1).replace(".", ",")} MB` : `${String(kb).replace(".", ",")} KB`;

const prettyDate = (iso) => (iso || "").split("-").reverse().join("/");

const FileCard = ({ file, tile }) => (
  <article
    data-testid={`kit-file-${file.name}`}
    className="group flex flex-col rounded-2xl border border-[#F5F1E8]/12 bg-[#F5F1E8]/[0.04] overflow-hidden transition-colors duration-300 hover:border-[#F4D35E]/50"
  >
    <div
      className={`h-36 flex items-center justify-center border-b p-5 ${TILE[tile]}`}
      aria-hidden="true"
    >
      {file.preview ? (
        <img
          src={file.path}
          alt=""
          loading="lazy"
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <FileCode2 size={28} className="text-[#F5F1E8]/45" />
      )}
    </div>

    <div className="flex-1 flex flex-col p-5">
      <h3 className="text-[#F5F1E8] text-sm font-semibold leading-snug">{file.title}</h3>
      <p className="text-[#F5F1E8]/45 text-xs mt-1.5 break-all">{file.name}</p>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-[#F4D35E] border border-[#F4D35E]/35 rounded-full px-2.5 py-1">
          {file.format}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.12em] uppercase rounded-full px-2.5 py-1 ${
            file.print
              ? "text-[#9FE0B8] border border-[#9FE0B8]/35"
              : "text-[#F5F1E8]/55 border border-[#F5F1E8]/20"
          }`}
        >
          {file.print ? <Printer size={11} /> : <Monitor size={11} />}
          {file.print ? "Imprenta" : "Digital"}
        </span>
        <span className="text-[#F5F1E8]/40 text-xs">{prettySize(file.kb)}</span>
      </div>

      <p className="text-[#F5F1E8]/55 text-xs mt-3 leading-relaxed flex-1">{file.note}</p>

      <a
        data-testid={`kit-download-${file.name}`}
        href={file.path}
        download
        className="mt-4 inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#F5F1E8] text-[#3B2523] text-sm font-semibold px-5 transition-colors duration-300 hover:bg-[#F4D35E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1B100E]"
      >
        <Download size={15} />
        Descargar
      </a>
    </div>
  </article>
);

const Kit = () => {
  const [kit, setKit] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch("/brand/kit/kit.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("no kit"))))
      .then(setKit)
      .catch(() => setFailed(true));
  }, []);

  return (
    <div data-testid="kit-page" className="min-h-screen bg-[#1B100E] overflow-x-hidden">
      <Seo
        title="Kit de marca · Naguarà"
        description="Material de marca de Naguarà para descargar. Página interna."
        path="/kit"
        lang="es"
        noindex
      />

      <main className="px-6 md:px-12 lg:px-20 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/"
            data-testid="kit-back-link"
            className="inline-flex items-center gap-2 min-h-[44px] text-[#F5F1E8]/60 text-sm font-semibold transition-colors hover:text-[#F5F1E8]"
          >
            <ArrowLeft size={16} />
            Volver a la portada
          </Link>

          <p className="flex items-center gap-2 mt-8 text-[#F4D35E] text-xs font-bold uppercase tracking-[0.22em]">
            <EyeOff size={13} />
            Página privada · no indexada
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#F5F1E8] mt-4 max-w-[720px] leading-[1.02]">
            Kit de marca Naguarà
          </h1>
          <p className="text-[#F5F1E8]/65 text-base md:text-lg mt-6 max-w-[620px] leading-relaxed">
            Todo el material oficial de la marca en un solo sitio: código QR, logotipos, iconos y
            la bolsa con sus etiquetas reales. Esta dirección no aparece en el menú, ni en el pie,
            ni en Google: solo la tiene quien tú le pases el enlace.
          </p>

          {failed && (
            <p
              data-testid="kit-error"
              role="alert"
              className="mt-10 rounded-2xl border border-[#F4D35E]/35 bg-[#F4D35E]/10 px-5 py-4 text-[#F4D35E] text-sm"
            >
              No hemos podido cargar el listado de archivos. Vuelve a cargar la página.
            </p>
          )}

          {kit && (
            <>
              <div
                data-testid="kit-zip-card"
                className="mt-12 flex flex-col md:flex-row md:items-center gap-6 rounded-3xl border border-[#F4D35E]/30 bg-[#F4D35E]/[0.07] p-7 md:p-9"
              >
                <div className="flex-1">
                  <h2 className="font-display text-xl md:text-2xl font-bold text-[#F5F1E8]">
                    Descargar todo el kit
                  </h2>
                  <p className="text-[#F5F1E8]/60 text-sm mt-2 leading-relaxed">
                    {kit.zip.files} archivos organizados en carpetas + un LEEME con qué vale para
                    imprenta y qué es solo digital. {prettySize(kit.zip.kb)} · actualizado el{" "}
                    {prettyDate(kit.generated)}.
                  </p>
                </div>
                <a
                  data-testid="kit-download-zip"
                  href={kit.zip.path}
                  download
                  className="inline-flex items-center justify-center gap-2 min-h-[52px] rounded-full bg-[#F4D35E] text-[#3B2523] font-bold px-8 transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1B100E]"
                >
                  <Download size={18} />
                  Descargar ZIP
                </a>
              </div>

              {kit.groups.map((group) => (
                <section
                  key={group.id}
                  data-testid={`kit-group-${group.id}`}
                  className="mt-16 md:mt-20"
                >
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[#F5F1E8]">
                    {group.title}
                  </h2>
                  <p className="text-[#F5F1E8]/60 text-sm md:text-base mt-3 max-w-[680px] leading-relaxed">
                    {group.text}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
                    {group.files.map((file) => (
                      <FileCard key={file.path} file={file} tile={group.tile} />
                    ))}
                  </div>
                </section>
              ))}

              <section data-testid="kit-print" className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-3xl border border-[#9FE0B8]/25 bg-[#9FE0B8]/[0.06] p-7 md:p-9">
                  <h2 className="inline-flex items-center gap-2.5 font-display text-xl md:text-2xl font-bold text-[#F5F1E8]">
                    <Printer size={20} className="text-[#9FE0B8]" />
                    Preparado para imprenta
                  </h2>
                  <p className="text-[#F5F1E8]/60 text-sm mt-3 leading-relaxed">
                    Estos archivos se pueden mandar a imprimir tal cual. Los vectoriales (SVG) no
                    tienen límite de tamaño; los demás llevan indicado hasta cuántos centímetros
                    aguantan a 300 ppp.
                  </p>
                  <ul data-testid="kit-print-ready" className="mt-5 space-y-2.5">
                    {kit.print_ready.map((name) => (
                      <li key={name} className="flex items-start gap-2.5 text-[#F5F1E8]/80 text-sm">
                        <Check size={15} className="text-[#9FE0B8] shrink-0 mt-0.5" />
                        <span className="break-all">{name}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-3xl border border-[#F5F1E8]/12 bg-[#F5F1E8]/[0.04] p-7 md:p-9">
                  <h2 className="inline-flex items-center gap-2.5 font-display text-xl md:text-2xl font-bold text-[#F5F1E8]">
                    <Clock size={20} className="text-[#F4D35E]" />
                    Pendiente de añadir
                  </h2>
                  <p className="text-[#F5F1E8]/60 text-sm mt-3 leading-relaxed">
                    No generamos versiones falsas de lo que todavía no existe. En cuanto tengas
                    estos archivos, los subimos aquí con el mismo formato.
                  </p>
                  <ul data-testid="kit-pending" className="mt-5 space-y-2.5">
                    {kit.pending.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-[#F5F1E8]/75 text-sm leading-relaxed">
                        <Clock size={15} className="text-[#F4D35E] shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div
                    data-testid="kit-photos-pending"
                    className="mt-6 rounded-2xl border border-dashed border-[#F5F1E8]/20 px-5 py-6 flex items-start gap-3"
                  >
                    <ImageIcon size={18} className="text-[#F5F1E8]/45 shrink-0 mt-0.5" />
                    <p className="text-[#F5F1E8]/60 text-sm leading-relaxed">
                      Hueco reservado para tus fotografías reales de producto y de marca. Mándalas
                      a <span className="text-[#F5F1E8]">{CONTACT_EMAIL}</span> en la máxima
                      calidad que tengas (originales de cámara o móvil, sin recortar) y las
                      publicamos en esta misma página.
                    </p>
                  </div>
                </div>
              </section>

              <p className="inline-flex items-center gap-2 mt-14 text-[#F5F1E8]/45 text-xs">
                <QrCode size={14} />
                El QR apunta a {kit.qr_target} · kit generado el {prettyDate(kit.generated)}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Kit;
